import { Button } from '@base-ui/react/button';
import { Link } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import buttonStyles from '@/admin/styles/button.module.css';
import api from '@/api';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { DenySwitchDialog } from './DenySwitchDialog';
import { useNotice } from './notice';

const Actions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

// A row click navigates to the student, so every control stops it.
const stop = (e: { stopPropagation: () => void }): void => e.stopPropagation();

type Helper = { id: number } | null | undefined;

/** Message Study Helper and Substitute: both exist only with a helper. */
export function HelperActions({
  studentId,
  helper,
  studyGroupSlug,
}: {
  studentId: number;
  helper: Helper;
  studyGroupSlug: string | null | undefined;
}): ReactElement | null {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  if (!helper) return null;
  return (
    <Actions onClick={stop}>
      {me && (
        <Link
          to="/members/$memberId/messages/compose/"
          params={{ memberId: String(me.id) }}
          search={{ r: helper.id }}
          onClick={stop}
        >
          {t('admin.waiting.action.messageHelper')}
        </Link>
      )}
      {studyGroupSlug && (
        // The substitute screen is the groups unit's route outside the SPA
        // router tree, so a plain href.
        <a
          href={`/groups/${studyGroupSlug}/admin/substitute/?substitute=${studentId}`}
          onClick={stop}
        >
          {t('admin.waiting.action.substitute')}
        </a>
      )}
    </Actions>
  );
}

// Another staff member may have settled the row first (a 409): say so and refetch, so
// the stale row leaves the list.
function useStaleRowNotice(
  refresh: () => Promise<unknown>,
): (setter?: () => void) => () => Promise<void> {
  const { t } = useTranslation();
  const notify = useNotice();
  return (setter) => async () => {
    setter?.();
    notify(t('admin.common.couldNotSave'));
    await refresh();
  };
}

const refreshRequests = async (): Promise<void> => {
  await api.listAdminAdoptionRequests.invalidateQueries();
  await api.listAdminAdoptionRequestHistory.invalidateQueries();
};

export function RejectAction({
  requestId,
}: {
  requestId: number;
}): ReactElement {
  const { t } = useTranslation();
  const notify = useNotice();
  const onStale = useStaleRowNotice(refreshRequests);
  const mutation = api.adminRejectAdoptionRequest.useMutation(undefined, {
    onSuccess: async () => {
      notify(t('admin.waiting.done.rejected'));
      await refreshRequests();
    },
    onError: onStale(),
  });
  return (
    <Actions onClick={stop}>
      <Button
        type="button"
        className={buttonStyles.Button}
        disabled={mutation.isPending}
        onClick={(e) => {
          stop(e);
          mutation.mutate({ path: { request_id: requestId } });
        }}
      >
        {t('admin.waiting.action.reject')}
      </Button>
    </Actions>
  );
}

export function SwitchActions({
  requestId,
  studentName,
  studentEmail,
}: {
  requestId: number;
  studentName: string;
  studentEmail: string;
}): ReactElement {
  const { t } = useTranslation();
  const notify = useNotice();
  const onStale = useStaleRowNotice(refreshRequests);
  const [denying, setDenying] = useState(false);
  const approve = api.approveSwitchRequest.useMutation(undefined, {
    onSuccess: async () => {
      notify(t('admin.common.done'));
      await refreshRequests();
    },
    onError: onStale(),
  });
  const deny = api.denySwitchRequest.useMutation(undefined, {
    onSuccess: async () => {
      setDenying(false);
      await refreshRequests();
    },
    onError: onStale(() => setDenying(false)),
  });
  const busy = approve.isPending || deny.isPending;
  return (
    <Actions onClick={stop}>
      <Button
        type="button"
        className={buttonStyles.Button}
        disabled={busy}
        onClick={(e) => {
          stop(e);
          approve.mutate({ path: { request_id: requestId } });
        }}
      >
        {t('admin.waiting.action.approve')}
      </Button>
      <Button
        type="button"
        className={buttonStyles.Button}
        disabled={busy}
        onClick={(e) => {
          stop(e);
          setDenying(true);
        }}
      >
        {t('admin.waiting.action.deny')}
      </Button>
      <DenySwitchDialog
        open={denying}
        studentName={studentName}
        studentEmail={studentEmail}
        pending={deny.isPending}
        onCancel={() => setDenying(false)}
        onConfirm={() => deny.mutate({ path: { request_id: requestId } })}
      />
    </Actions>
  );
}

export function PassOnAction({
  studentId,
}: {
  studentId: number;
}): ReactElement {
  const { t } = useTranslation();
  const notify = useNotice();
  const onStale = useStaleRowNotice(() =>
    api.listAdminHopefulStudents.invalidateQueries(),
  );
  const mutation = api.adminPassOnStudent.useMutation(undefined, {
    onSuccess: async () => {
      notify(t('admin.waiting.done.passedOn'));
      await api.listAdminHopefulStudents.invalidateQueries();
    },
    onError: onStale(),
  });
  return (
    <Actions onClick={stop}>
      <Button
        type="button"
        className={buttonStyles.Button}
        disabled={mutation.isPending}
        onClick={(e) => {
          stop(e);
          mutation.mutate({ path: { student_id: studentId } });
        }}
      >
        {t('admin.waiting.action.passOn')}
      </Button>
    </Actions>
  );
}
